import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import SalesButton from '../../components/_atoms/SalesButton/SalesButton'
import SalesText from '../../components/_atoms/SalesText/SalesText'
import { colors, fontSizes, fontWeights } from '../../constants/salesStyles'
import { useGetAnalyticsPartsAddedMutation } from '../../modules/store/datahubApi/datahubApi'
import { adminRouteAlias, getAdminNavigationPath } from '../../router/adminRouteAlias'

import styles from './styles.module.scss'

type PeriodMode = 'month' | 'range'
type SortField = 'date' | 'manager' | 'count'
type SortDirection = 'asc' | 'desc'

const toDate = (value: string) => new Date(`${value}T00:00:00`)
const formatAxisDate = (value: string) => {
    const date = toDate(value)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    return `${day}.${month}`
}

const formatMonthValue = (date: Date) => {
    const year = date.getFullYear()
    const month = `${date.getMonth() + 1}`.padStart(2, '0')
    return `${year}-${month}`
}

const monthLabel = (monthValue: string) => {
    const [year, month] = monthValue.split('-').map(Number)
    if (!year || !month) return ''
    return new Date(year, month - 1, 1).toLocaleString('ru-RU', { month: 'long', year: 'numeric' })
}

const getCurrentMonthValue = () => formatMonthValue(new Date())

const getMonthRange = (monthValue: string) => {
    const [year, month] = monthValue.split('-').map(Number)
    if (!year || !month) return null
    const monthStart = `${year}-${String(month).padStart(2, '0')}-01`
    const daysInMonth = new Date(year, month, 0).getDate()
    const monthEnd = `${year}-${String(month).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`

    return { from: monthStart, to: monthEnd }
}

const AnalyticsParts = () => {
    const [periodMode, setPeriodMode] = useState<PeriodMode>('month')
    const [monthValue, setMonthValue] = useState(getCurrentMonthValue())
    const [rangeFrom, setRangeFrom] = useState('')
    const [rangeTo, setRangeTo] = useState('')
    const [selectedManagers, setSelectedManagers] = useState<string[]>([])
    const [sortField, setSortField] = useState<SortField>('date')
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
    const [getAnalyticsPartsAdded, { data: analyticsPartsData, isLoading: isAnalyticsPartsLoading, isError }] =
        useGetAnalyticsPartsAddedMutation()

    const selectedPeriod = useMemo(() => {
        if (periodMode === 'month') return getMonthRange(monthValue)
        if (!rangeFrom || !rangeTo || rangeFrom > rangeTo) return null
        return { from: rangeFrom, to: rangeTo }
    }, [monthValue, periodMode, rangeFrom, rangeTo])

    const allManagers = useMemo(() => {
        const fromTotals = analyticsPartsData?.data?.totals?.managers?.map(item => item.manager) || []
        if (fromTotals.length > 0) return fromTotals

        const fromChart = analyticsPartsData?.data?.chartByManager?.map(item => item.manager) || []
        if (fromChart.length > 0) return fromChart

        const fromTable = analyticsPartsData?.data?.table?.map(item => item.manager) || []
        return Array.from(new Set(fromTable))
    }, [analyticsPartsData])

    useEffect(() => {
        if (!selectedPeriod) return

        getAnalyticsPartsAdded({
            period: selectedPeriod,
            manager: selectedManagers.length === 0 ? [] : selectedManagers,
        })
    }, [getAnalyticsPartsAdded, selectedManagers, selectedPeriod])

    const partsByDay = useMemo(() => {
        if (!selectedPeriod) return []
        return analyticsPartsData?.data?.chartByDay || []
    }, [analyticsPartsData, selectedPeriod])

    const partsByManager = useMemo(() => {
        if (!selectedPeriod) return []
        return analyticsPartsData?.data?.chartByManager || []
    }, [analyticsPartsData, selectedPeriod])

    const tableRows = useMemo(() => {
        if (!selectedPeriod) return []
        return analyticsPartsData?.data?.table || []
    }, [analyticsPartsData, selectedPeriod])

    const addedCountTotal = analyticsPartsData?.data?.totals?.addedCount || 0
    const isChartsLoading = isAnalyticsPartsLoading && !!selectedPeriod && partsByDay.length === 0
    const isTableLoading = isAnalyticsPartsLoading && !!selectedPeriod && tableRows.length === 0

    const sortedRows = useMemo(() => {
        const rows = [...tableRows]
        rows.sort((a, b) => {
            const direction = sortDirection === 'asc' ? 1 : -1
            switch (sortField) {
                case 'date':
                    return a.date.localeCompare(b.date) * direction
                case 'manager':
                    return a.manager.localeCompare(b.manager) * direction
                case 'count':
                    return (a.count - b.count) * direction
                default:
                    return 0
            }
        })
        return rows
    }, [sortDirection, sortField, tableRows])

    const isAllManagersSelected = selectedManagers.length === 0 || selectedManagers.length === allManagers.length

    const toggleAllManagers = () => {
        setSelectedManagers(prev => (prev.length === 0 || prev.length === allManagers.length ? [] : allManagers))
    }

    const toggleManager = (manager: string) => {
        setSelectedManagers(prev => (prev.includes(manager) ? prev.filter(item => item !== manager) : [...prev, manager]))
    }

    const shiftMonth = (diff: number) => {
        const [year, month] = monthValue.split('-').map(Number)
        if (!year || !month) return
        const next = new Date(year, month - 1 + diff, 1)
        setMonthValue(formatMonthValue(next))
    }

    const resetMonth = () => setMonthValue(getCurrentMonthValue())

    const sortIndicator = (field: SortField) => (sortField === field ? (sortDirection === 'asc' ? '^' : 'v') : '')

    const toggleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
            return
        }
        setSortField(field)
        setSortDirection('asc')
    }

    return (
        <div className={styles.wrap}>
            <SalesText text="Аналитика по запчастям" fontSize={fontSizes.xxl} fontWeight={fontWeights.bold} />
            <div className={styles.breadcrumbs}>
                <Link className={styles.breadcrumbLink} to={getAdminNavigationPath(adminRouteAlias.analytics.dashboard)}>
                    Аналитика
                </Link>
                <span className={styles.breadcrumbSeparator}>/</span>
                <span className={styles.breadcrumbCurrent}>Аналитика по запчастям</span>
            </div>

            <div className={styles.section}>
                <div className={styles.sectionTitle}>Фильтры</div>
                <div className={styles.filters}>
                    <div className={styles.filterGroup}>
                        <label className={styles.filterLabel}>Период</label>
                        <select
                            className={styles.input}
                            value={periodMode}
                            onChange={e => setPeriodMode(e.target.value as PeriodMode)}
                        >
                            <option value="month">Месяц</option>
                            <option value="range">Диапазон дат</option>
                        </select>
                    </div>
                    {periodMode === 'month' ? (
                        <div className={styles.filterGroup}>
                            <label className={styles.filterLabel}>Месяц</label>
                            <div className={styles.monthControls}>
                                <SalesButton
                                    borderRadius={10}
                                    backgroundColor={colors.grayDark}
                                    paddingHorizontalPixels={12}
                                    paddingVerticalPixels={7}
                                    onClick={() => shiftMonth(-1)}
                                >
                                    <ChevronLeft size={18} color={colors.white} />
                                </SalesButton>
                                <SalesButton
                                    borderRadius={10}
                                    backgroundColor={colors.grayDark}
                                    paddingHorizontalPixels={12}
                                    paddingVerticalPixels={7}
                                    onClick={resetMonth}
                                >
                                    <SalesText
                                        className={styles.monthLabel}
                                        text={monthLabel(monthValue)}
                                        fontSize={fontSizes.s}
                                        color={colors.white}
                                    />
                                </SalesButton>
                                <SalesButton
                                    borderRadius={10}
                                    backgroundColor={colors.grayDark}
                                    paddingHorizontalPixels={12}
                                    paddingVerticalPixels={7}
                                    onClick={() => shiftMonth(1)}
                                >
                                    <ChevronRight size={18} color={colors.white} />
                                </SalesButton>
                            </div>
                        </div>
                    ) : (
                        <div className={styles.rangeGroup}>
                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>Дата от</label>
                                <input
                                    className={styles.input}
                                    type="date"
                                    value={rangeFrom}
                                    onChange={e => setRangeFrom(e.target.value)}
                                />
                            </div>
                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>Дата до</label>
                                <input
                                    className={styles.input}
                                    type="date"
                                    value={rangeTo}
                                    onChange={e => setRangeTo(e.target.value)}
                                />
                            </div>
                        </div>
                    )}
                </div>
                {/* TODO: временно скрыто по задаче: фильтр менеджеров
                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Менеджеры</label>
                    <div className={styles.checkboxList}>
                        <label className={styles.checkboxItem}>
                            <input type="checkbox" checked={isAllManagersSelected} onChange={toggleAllManagers} />
                            <span>Все</span>
                        </label>
                        {allManagers.map(manager => (
                            <label key={manager} className={styles.checkboxItem}>
                                <input
                                    type="checkbox"
                                    checked={selectedManagers.includes(manager)}
                                    onChange={() => toggleManager(manager)}
                                />
                                <span>{manager}</span>
                            </label>
                        ))}
                    </div>
                </div>
                */}
            </div>
            {periodMode === 'range' && !selectedPeriod && (
                <div className={styles.section}>
                    <div className={styles.emptyState}>Выберите корректный диапазон дат</div>
                </div>
            )}
            {isError && (
                <div className={styles.section}>
                    <div className={styles.emptyState}>Ошибка загрузки данных аналитики</div>
                </div>
            )}

            <div className={styles.section}>
                <div className={styles.sectionTitle}>Кол-во добавленных запчастей по дням ({addedCountTotal})</div>
                <div className={styles.chart}>
                    {isChartsLoading ? (
                        <div className={`${styles.skeletonPulse} ${styles.skeletonChart}`} />
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={partsByDay}>
                                <CartesianGrid vertical={false} />
                                <XAxis dataKey="date" interval={0} tickFormatter={formatAxisDate} />
                                <YAxis allowDecimals={false} />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'rgba(96, 97, 108, 0.85)',
                                        borderRadius: 10,
                                        border: 'none',
                                        color: colors.white,
                                    }}
                                    itemStyle={{ color: colors.white }}
                                    labelStyle={{ color: colors.white }}
                                />
                                <Bar dataKey="count" fill={colors.pinkDark} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            {/* TODO: временно скрыто по задаче: график по менеджерам
            <div className={styles.section}>
                <div className={styles.sectionTitle}>Добавленные запчасти по менеджерам</div>
                <div className={styles.chart}>
                    {isChartsLoading ? (
                        <div className={`${styles.skeletonPulse} ${styles.skeletonChart}`} />
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={partsByManager}>
                                <CartesianGrid vertical={false} />
                                <XAxis dataKey="manager" />
                                <YAxis allowDecimals={false} />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'rgba(96, 97, 108, 0.85)',
                                        borderRadius: 10,
                                        border: 'none',
                                        color: colors.white,
                                    }}
                                    itemStyle={{ color: colors.white }}
                                    labelStyle={{ color: colors.white }}
                                />
                                <Bar dataKey="count" fill={colors.green} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>
            */}

            <div className={styles.section}>
                <div className={styles.sectionTitle}>Данные по запчастям</div>
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>
                                    <button className={styles.sortButton} onClick={() => toggleSort('date')}>
                                        Дата <span className={styles.sortIndicator}>{sortIndicator('date')}</span>
                                    </button>
                                </th>
                                <th>
                                    <button className={styles.sortButton} onClick={() => toggleSort('count')}>
                                        Кол-во <span className={styles.sortIndicator}>{sortIndicator('count')}</span>
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {isTableLoading &&
                                Array.from({ length: 6 }).map((_, index) => (
                                    <tr key={`skeleton-row-${index}`}>
                                        <td><div className={`${styles.skeletonPulse} ${styles.skeletonTableRow}`} /></td>
                                        <td><div className={`${styles.skeletonPulse} ${styles.skeletonTableRow}`} /></td>
                                    </tr>
                                ))}
                            {!isTableLoading &&
                                sortedRows.map(row => (
                                    <tr key={`${row.date}-${row.manager}`}>
                                        <td>{row.date}</td>
                                        <td>{row.count}</td>
                                    </tr>
                                ))}
                            {!isTableLoading && sortedRows.length === 0 && (
                                <tr>
                                    <td colSpan={2} className={styles.emptyState}>
                                        Нет данных за выбранный период
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

export default AnalyticsParts
